import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
// Services
import { useGetAllContentTypesQuery } from '../../services/cortizol/content-type/contentTypeQueries';
import { getAllContents } from '../../services/cortizol/content/contentService';

const useGlobalSearch = () => {
  // States and const
  const [searchValue, setSearchValue] = useState('');

  // API calls
  const { data } = useGetAllContentTypesQuery();
  const getContentsListQueries = useQueries({
    queries: searchValue ? data.data.map((contentType) => ({
      queryKey: [`contents-${contentType.id}`, searchValue],
      queryFn: async () => {
        const res = await getAllContents({
          pageNumber: 1,
          pageSize: 9999,
          contentTypeId: contentType.id,
          search: searchValue,
        });
        return { ...res, contentTypeId: contentType.id };
      },
    })) : [],
  });

  // Hooks
  const navigate = useNavigate();

  // Private Methods
  /**
   * Handle search change
   * @param {React.ChangeEvent<HTMLInputElement>} e
   * @returns {void}
   */
  const onSearchChange = (e) => setSearchValue(e.target.value);

  // Public const
  const areSearchQueriesLoading = useMemo(
    () => getContentsListQueries.some((query) => query.isLoading),
    [getContentsListQueries],
  );
  const searchResultsOptions = useMemo(() => getContentsListQueries
    .map((query) => (query.isSuccess ? ({
      label: data?.data.find((contentType) => contentType.id === query.data.contentTypeId)?.schema.title || '',
      categoryValue: query.data.contentTypeId,
      options: query.data.data.map((content) => ({
        label: content.name,
        value: content.id,
      })) || [],
    }) : null))
    .filter((option) => option?.options?.length > 0), [getContentsListQueries, data]);

  // Public Methods
  const handleSearchChange = debounce(onSearchChange, 1000);
  /**
   * Handle search result click
   * @param {string} contentId
   * @param {string} contentTypeId
   * @returns {void}
   */
  const handleSearchResultClick = (contentId, contentTypeId) => {
    navigate(`/content/${contentTypeId}/${contentId}`);
  };

  return {
    searchValue,
    handleSearchChange,
    areSearchQueriesLoading,
    searchResultsOptions,
    handleSearchResultClick,
  };
};

export default useGlobalSearch;
